import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../../../components/layout/DashboardLayout';
import DataTable from '../../../../components/ui/DataTable';
import { LoadingPage } from '../../../../components/ui/LoadingSpinner';
import apiClient from '../../../../lib/api';
import { normalizeListPagination } from '../../../../lib/normalizeListPagination';
import { toast } from 'react-hot-toast';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Drawer,
  Divider,
  TextField,
  IconButton,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

const CompanyBranchesPage = () => {
  const navigate = useNavigate();
  const { id: companyId } = useParams();
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [isBranchDrawerOpen, setIsBranchDrawerOpen] = useState(false);
  const [branchFormData, setBranchFormData] = useState({
    branchName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    contactPersonName: '',
  });
  const [branchErrors, setBranchErrors] = useState({});
  const [isCreatingBranch, setIsCreatingBranch] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Load company data
  const fetchCompany = async () => {
    try {
      const response = await apiClient.getFuneralCompany(companyId);
      const companyData = response.data || response;
      setCompany(companyData);
    } catch (error) {
      console.error('Error fetching company:', error);
      toast.error('Failed to load company');
    }
  };

  // Load branches data filtered by company
  const loadBranches = async (searchParams) => {
    try {
      setIsLoading(true);
      
      const params = {
        page: (searchParams?.page ?? 1),
        limit: (searchParams?.rowsPerPage ?? 20),
        companyId: companyId, // Always filter by company ID
      };
      
      // Add search parameter
      if (searchParams?.searchTerm) {
        params.search = searchParams.searchTerm;
      }
      
      console.log('Loading branches with params:', params);
      
      const response = await apiClient.getBranches(params);
      console.log('API Response:', response);
      
      setBranches(response.data || []);
      setPagination(normalizeListPagination(response, { limit: 20 }));
    } catch (error) {
      console.error('Error loading branches:', error);
      toast.error('Failed to load branches');
      setBranches([]);
      setPagination({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchCompany();
      loadBranches();
    }
  }, [companyId]);

  // Handle search from DataTable
  const handleSearch = (searchParams) => {
    console.log('Search params:', searchParams);
    loadBranches(searchParams);
  };

  const handleRowSelect = (branch) => {
    // Navigate to nested branch detail route
    navigate(`/companies/${companyId}/branches/${branch.id}`);
  };

  const handleAddBranch = () => {
    setIsBranchDrawerOpen(true);
    setBranchFormData({
      branchName: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      email: '',
      contactPersonName: '',
    });
    setBranchErrors({});
  };

  const handleCloseBranchDrawer = () => {
    setIsBranchDrawerOpen(false);
    setBranchFormData({
      branchName: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      email: '',
      contactPersonName: '',
    });
    setBranchErrors({});
  };

  const handleBranchInputChange = (field, value) => {
    setBranchFormData(prev => ({ ...prev, [field]: value }));
    if (branchErrors[field]) {
      setBranchErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateBranchForm = () => {
    const newErrors = {};

    if (!branchFormData.branchName.trim()) {
      newErrors.branchName = 'Branch name is required';
    }
    if (!branchFormData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!branchFormData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!branchFormData.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!branchFormData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    }

    setBranchErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitBranch = async () => {
    if (!validateBranchForm()) {
      toast.error('Please fix all validation errors');
      return;
    }

    setIsCreatingBranch(true);
    try {
      // Clean up the data - only include non-empty optional fields
      const submitData = {
        companyId: companyId,
        branchName: branchFormData.branchName,
        address: branchFormData.address,
        city: branchFormData.city,
        state: branchFormData.state,
        zipCode: branchFormData.zipCode,
      };

      // Only add optional fields if they have values
      if (branchFormData.contactPersonName.trim()) {
        submitData.contactPersonName = branchFormData.contactPersonName;
      }
      if (branchFormData.phone.trim()) {
        submitData.phone = branchFormData.phone;
      }
      if (branchFormData.email.trim()) {
        submitData.email = branchFormData.email;
      }

      console.log('Submitting branch data:', submitData);
      await apiClient.createBranch(submitData);
      
      toast.success('Branch created successfully!');
      setIsBranchDrawerOpen(false);
      loadBranches(); // Refresh the branches list
    } catch (error) {
      console.error('Error creating branch:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create branch';
      toast.error(errorMessage);
    } finally {
      setIsCreatingBranch(false);
    }
  };

  // Branch table columns
  const branchColumns = [
    {
      id: 'branchName',
      label: 'Branch Name',
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {value || 'Unnamed Branch'}
        </Typography>
      ),
    },
    {
      id: 'address',
      label: 'Address',
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" color="text.secondary">
          {`${row.address || ''}, ${row.city || ''}, ${row.state || ''} ${row.zipCode || ''}`.replace(/,\s*,/g, ',').replace(/,\s*$/, '') || 'No address'}
        </Typography>
      ),
    },
    {
      id: 'phone',
      label: 'Phone',
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2">
          {value || '-'}
        </Typography>
      ),
    },
    {
      id: 'isActive',
      label: 'Status',
      sortable: true,
      render: (value, row) => (
        <Chip 
          label={row.isActive ? 'Active' : 'Inactive'} 
          size="small" 
          color={row.isActive ? 'success' : 'warning'}
          variant={row.isActive ? 'filled' : 'outlined'}
        />
      ),
    },
  ];

  // Branch filters
  const branchFilters = [
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search branches...'
    },
  ];

  if (isLoading && !company) {
    return <LoadingPage />;
  }

  return (
    <DashboardLayout title={`Branches - ${company?.companyName || 'Company'}`}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1">
              Branches - {company?.companyName || 'Company'}
            </Typography>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2
          }}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4a8f 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Branches
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {pagination.total}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  All locations
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ 
              background: 'linear-gradient(135deg, #d885e8 0%, #e04a5f 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Branches
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {branches.filter(b => b.isActive).length}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Currently operational
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ 
              background: 'linear-gradient(135deg, #3dd06f 0%, #32e0c0 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Inactive Branches
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {branches.filter(b => !b.isActive).length}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Not operational
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Branches Table */}
        <Box sx={{ mt: 4 }}>         
          <DataTable
            data={branches}
            title="Branches"
            columns={branchColumns}
            filters={branchFilters}
            onSelect={handleRowSelect}
            onSearch={handleSearch}
            totalPages={pagination.totalPages}
            currentPage={pagination.page}
            defaultRowsPerPage={20}
            loading={isLoading}
            onRowClick={(row) => navigate(`/companies/${companyId}/branches/${row.id}`)}
            onAdd={handleAddBranch}
            addButtonText="Add Branch"
          />
        </Box>
      </Box>

      {/* Add Branch Drawer */}
      <Drawer
        anchor="right"
        open={isBranchDrawerOpen}
        onClose={handleCloseBranchDrawer}
        PaperProps={{
          sx: { 
            width: { xs: '100%', sm: 500 },
            backgroundColor: '#ffffff'
          }
        }}
      >
        <Box sx={{ p: 3, backgroundColor: '#ffffff' }}>
          {/* Drawer Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Add New Branch
            </Typography>
            <IconButton onClick={handleCloseBranchDrawer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Branch Form */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Branch Name */}
            <TextField
              fullWidth
              label="Branch Name"
              value={branchFormData.branchName}
              onChange={(e) => handleBranchInputChange('branchName', e.target.value)}
              error={!!branchErrors.branchName}
              helperText={branchErrors.branchName}
              required
            />

            {/* Address Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Address
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={branchFormData.address}
                  onChange={(e) => handleBranchInputChange('address', e.target.value)}
                  error={!!branchErrors.address}
                  helperText={branchErrors.address}
                  required
                />

                <TextField
                  fullWidth
                  label="City"
                  value={branchFormData.city}
                  onChange={(e) => handleBranchInputChange('city', e.target.value)}
                  error={!!branchErrors.city}
                  helperText={branchErrors.city}
                  required
                />

                <TextField
                  fullWidth
                  label="State"
                  value={branchFormData.state}
                  onChange={(e) => handleBranchInputChange('state', e.target.value)}
                  error={!!branchErrors.state}
                  helperText={branchErrors.state}
                  required
                />

                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={branchFormData.zipCode}
                  onChange={(e) => handleBranchInputChange('zipCode', e.target.value)}
                  error={!!branchErrors.zipCode}
                  helperText={branchErrors.zipCode}
                  required
                />
              </Box>
            </Box>

            {/* Contact Details Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Contact Details (Optional)
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Contact Person Name"
                  value={branchFormData.contactPersonName}
                  onChange={(e) => handleBranchInputChange('contactPersonName', e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Phone Number"
                  value={branchFormData.phone}
                  onChange={(e) => handleBranchInputChange('phone', e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={branchFormData.email}
                  onChange={(e) => handleBranchInputChange('email', e.target.value)}
                />
              </Box>
            </Box>
          </Box>

          {/* Drawer Action Button */}
          <Box sx={{ mt: 4 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmitBranch}
              disabled={isCreatingBranch}
              startIcon={<SaveIcon />}
            >
              {isCreatingBranch ? 'Creating...' : 'Create Branch'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </DashboardLayout>
  );
};

export default CompanyBranchesPage;
